import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { getApiErrorMessage } from "../lib/apiErrors";
import { isMaxSpeedMissing, isMaxSpeedRequired, maxSpeedHelperKey, maxSpeedValidationKey } from "../lib/aircraftMaxSpeed";
import { isMtomMissing, isMtomRequired, missingSoraInputsKeys, mtomHelperKey, mtomValidationKey } from "../lib/aircraftMtom";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Plane, Plus, MoreVertical, Pencil, Trash2, Search, Wrench,
  Upload, FileText, Download, Image, Paperclip, RefreshCw, FileSpreadsheet, Eye, Wifi,
  ChevronDown, ChevronUp, Clock, Calendar, Cpu, Puzzle, Check, X, Minus, Ruler, AlertTriangle
} from "lucide-react";
import { ImportWizard } from "../components/ImportWizard";
import RemoteIDStatusModal from "../components/aircraft/RemoteIDStatusModal";
import { AircraftExpandableRow } from "../components/aircraft/AircraftExpandableRow";
import { CharacteristicDimensionField } from "../components/aircraft/CharacteristicDimensionField";
import { FilterChips } from "../components/shared/FilterChips";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

/** Heading of one field group in the aircraft dialog. */
const FormGroupHeading = ({ children }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">{children}</h3>
);

export const Aircraft = () => {
  const { user } = useAuth();
  const { activeOrganizationId, isPlatformAdmin, registerScopeChangeGuard } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [aircraft, setAircraft] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showDocsDialog, setShowDocsDialog] = useState(false);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("maintenance");
  const fileInputRef = useRef(null);
  const [editingAircraft, setEditingAircraft] = useState(null);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showRidModal, setShowRidModal] = useState(false);
  const [ridAircraft, setRidAircraft] = useState(null);
  // Block D: Expandable row state
  const [expandedRowId, setExpandedRowId] = useState(null);
  // Block I-1: Status filter state
  const [statusFilter, setStatusFilter] = useState("all");
  // SORA characteristic-dimension section: open by default (unlike U-Space's optional fields, this one
  // is required for new aircraft — collapsed-by-default would hide a required input from the user and,
  // since the section unmounts its content when closed, from HTML's own `required` validation too).
  const [dimensionSectionOpen, setDimensionSectionOpen] = useState(true);
  // Manual diagram-type selector fallback — a UI aid only (which help diagram to show), never persisted
  // and never used to infer the characteristic-dimension value itself.
  const [manualDiagramKey, setManualDiagramKey] = useState(null);
  const [formData, setFormData] = useState({
    organization_id: "",
    model: "",
    serial_number: "",
    firmware_version: "",
    service_interval: 100,
    flight_hours: 0,
    last_maintenance_date: "",
    registration_number: "",
    // NEW: MTOM and UA Class
    mtom_kg: "",
    max_speed_ms: "",
    max_characteristic_dimension_m: "",
    legacy_drone: false,
    // SORA Equipment flags
    equipment: {
      parachute: false,
      flight_termination_system: false,
      geo_awareness: false,
      remote_id: false
    }
  });

  // Compute UA class from MTOM
  const computeUaClass = (mtomKg, legacyDrone) => {
    if (legacyDrone) return t.fleet?.noClassMarking || "No class marking";
    if (!mtomKg || mtomKg === "") return "";
    const mtom = parseFloat(mtomKg);
    if (isNaN(mtom)) return "";
    if (mtom < 0.25) return "C0";
    if (mtom < 0.9) return "C1";
    if (mtom < 4.0) return "C2";
    if (mtom < 25.0) return "C3";
    return "C4";
  };

  // Service cycle calculation helper - NOW USES MAX(hours_pct, days_pct) (Block M-3 update)
  // Bar represents SERVICE DUE-NESS = whichever axis is closest to limit
  // Unified thresholds: amber >= 80%, red >= 100%, calm/neutral below 80%
  const getServiceCycleInfo = (ac) => {
    const trigger = ac.maintenance_trigger || {};
    
    // If backend provides trigger data, use it directly
    if (trigger.hours_pct !== undefined && trigger.days_pct !== undefined) {
      const hoursPct = trigger.hours_pct || 0;
      const daysPct = trigger.days_pct || 0;
      const reason = trigger.reason || "none";
      
      // Drive bar from max(hours_pct, days_pct) - whichever is closer to limit
      const pct = Math.max(hoursPct, daysPct);
      
      // Determine which axis drives (for label)
      // Use reason from backend; for "both", default to hours
      const isDrivenByHours = reason === "hours" || reason === "both" || reason === "none" || hoursPct >= daysPct;
      
      // Unified thresholds (80%/100%)
      let status = "good";
      let color = "bg-emerald-500";
      if (pct >= 100) {
        status = "critical";
        color = "bg-red-500";
      } else if (pct >= 80) {
        status = "warning";
        color = "bg-amber-500";
      }
      
      // Return values based on driving axis for label display
      if (isDrivenByHours) {
        return {
          usagePercent: Math.min(100, Math.round(pct)),
          current: Math.round(trigger.hours_used || 0),
          limit: trigger.interval_hours || 100,
          unit: "hours", // for i18n lookup
          status,
          color,
          drivingAxis: "hours"
        };
      } else {
        return {
          usagePercent: Math.min(100, Math.round(pct)),
          current: trigger.days_since || 0,
          limit: trigger.interval_days || 180,
          unit: "days", // for i18n lookup
          status,
          color,
          drivingAxis: "calendar"
        };
      }
    }
    
    // Fallback to legacy calculation if no backend trigger data
    const flightHours = ac.flight_hours || 0;
    const serviceInterval = ac.service_interval || 100;
    
    let hoursSinceMaintenance = flightHours;
    if (ac.last_maintenance_date && ac.maintenance_flight_hours !== undefined) {
      hoursSinceMaintenance = flightHours - (ac.maintenance_flight_hours || 0);
    }
    
    const usagePercent = Math.min(100, (hoursSinceMaintenance / serviceInterval) * 100);
    
    let status = "good";
    let color = "bg-emerald-500";
    if (usagePercent >= 100) {
      status = "critical";
      color = "bg-red-500";
    } else if (usagePercent >= 80) {
      status = "warning";
      color = "bg-amber-500";
    }
    
    return {
      usagePercent: Math.round(usagePercent),
      current: Math.round(hoursSinceMaintenance),
      limit: serviceInterval,
      unit: "hours",
      status,
      color,
      drivingAxis: "hours"
    };
  };

  // Service Cycle Progress Component - REUSED, updated for max(hours, days) (Block M-3 update)
  const ServiceCycleIndicator = ({ aircraft }) => {
    const info = getServiceCycleInfo(aircraft);
    const units = t.fleet?.serviceBarUnits || { hours: "h", days: "d" };
    const unitLabel = info.unit === "hours" ? units.hours : units.days;
    
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-16">
          <div className="relative h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div 
              className={`absolute left-0 top-0 h-full ${info.color} rounded-full`}
              style={{ width: `${Math.min(info.usagePercent, 100)}%` }}
              role="progressbar"
              aria-valuenow={info.usagePercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Service cycle: ${info.current}/${info.limit}${unitLabel}`}
            />
          </div>
        </div>
        <span className={`text-xs tabular-nums whitespace-nowrap ${
          info.status === "critical" ? "text-rose-600 dark:text-rose-400" : 
          info.status === "warning" ? "text-amber-600 dark:text-amber-400" : 
          "text-slate-500 dark:text-slate-400"
        }`}>
          {info.current}/{info.limit}{unitLabel}
        </span>
      </div>
    );
  };

  const fetchData = useCallback(async () => {
    try {
      const [aircraftRes, orgsRes] = await Promise.all([
        api.get(`/aircraft`),
        api.get(`/organizations`)
      ]);
      setAircraft(aircraftRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      toast.error(t.messages.fetchError);
    } finally {
      setLoading(false);
    }
  }, [ t.messages.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showDialog && !showRidModal) return undefined;
    return registerScopeChangeGuard(() => window.confirm(t.organizationScope?.formDirtyConfirm));
  }, [showDialog, showRidModal, registerScopeChangeGuard, t.organizationScope?.formDirtyConfirm]);

  /**
   * Build partial payload for PUT /api/aircraft/{id}
   * Only includes fields that changed from the original aircraft.
   * This prevents E1's presence-based RBAC gate from triggering on unchanged equipment.
   */
  const buildPartialPayload = (original, current) => {
    const payload = {};
    
    // Simple fields - include only if changed
    const simpleFields = [
      'organization_id', 'model', 'serial_number', 'registration_number',
      'service_interval', 'flight_hours', 'last_maintenance_date',
      'mtom_kg', 'max_speed_ms', 'max_characteristic_dimension_m', 'legacy_drone'
    ];

    for (const field of simpleFields) {
      let originalValue = original[field];
      let currentValue = current[field];

      // Normalize values for comparison
      if (field === 'service_interval') {
        originalValue = parseInt(originalValue) || 100;
        currentValue = parseInt(currentValue) || 100;
      } else if (field === 'flight_hours') {
        originalValue = parseFloat(originalValue) || 0;
        currentValue = parseFloat(currentValue) || 0;
      } else if (field === 'mtom_kg' || field === 'max_speed_ms' || field === 'max_characteristic_dimension_m') {
        originalValue = originalValue ? parseFloat(originalValue) : null;
        currentValue = currentValue ? parseFloat(currentValue) : null;
      } else if (field === 'legacy_drone') {
        originalValue = originalValue || false;
        currentValue = currentValue || false;
      } else if (field === 'last_maintenance_date') {
        originalValue = originalValue?.split("T")[0] || "";
        currentValue = currentValue || "";
      } else {
        originalValue = originalValue || "";
        currentValue = currentValue || "";
      }
      
      if (originalValue !== currentValue) {
        // Apply proper type conversion for payload
        if (field === 'service_interval') {
          payload[field] = parseInt(currentValue) || 100;
        } else if (field === 'flight_hours') {
          payload[field] = parseFloat(currentValue) || 0;
        } else if (field === 'mtom_kg' || field === 'max_speed_ms' || field === 'max_characteristic_dimension_m') {
          payload[field] = currentValue ? parseFloat(currentValue) : null;
        } else if (field === 'legacy_drone') {
          payload[field] = currentValue || false;
        } else {
          payload[field] = currentValue;
        }
      }
    }
    
    // Firmware fields - include only if changed (E1 gated)
    const firmwareFields = ['firmware_version', 'firmware_up_to_date'];
    for (const field of firmwareFields) {
      const originalValue = original[field] ?? "";
      const currentValue = current[field] ?? "";
      if (originalValue !== currentValue) {
        payload[field] = currentValue;
      }
    }
    
    // Equipment - include ONLY if at least one SORA flag changed (E1 gated)
    const originalEquipment = original.equipment || {};
    const currentEquipment = current.equipment || {};
    const equipmentFlags = ['parachute', 'flight_termination_system', 'geo_awareness', 'remote_id'];
    
    let equipmentChanged = false;
    for (const flag of equipmentFlags) {
      const origVal = originalEquipment[flag] || false;
      const currVal = currentEquipment[flag] || false;
      if (origVal !== currVal) {
        equipmentChanged = true;
        break;
      }
    }
    
    if (equipmentChanged) {
      payload.equipment = {
        parachute: currentEquipment.parachute || false,
        flight_termination_system: currentEquipment.flight_termination_system || false,
        geo_awareness: currentEquipment.geo_awareness || false,
        remote_id: currentEquipment.remote_id || false
      };
    }
    
    return payload;
  };

  // Focuses/scrolls to a field and shows a localized error toast — shared by every required-field
  // check in handleSubmit below (form has noValidate, so nothing native does this for us anymore).
  const failValidation = (message, fieldId) => {
    toast.error(message);
    setTimeout(() => {
      const el = document.getElementById(fieldId);
      el?.focus();
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.model?.trim()) {
      failValidation(t.fleet?.modelRequired || "Aircraft model is required.", "ac_model");
      return;
    }
    if (!formData.serial_number?.trim()) {
      failValidation(t.fleet?.serialNumberRequired || "Serial number is required.", "ac_serial");
      return;
    }
    // MTOM and speed are required on create; an aircraft stored without them stays editable (the dialog only warns).
    const mtomErrorKey = mtomValidationKey(formData.mtom_kg, editingAircraft);
    if (mtomErrorKey) {
      failValidation(t.fleet?.[mtomErrorKey] || "MTOM must be a positive number of kg.", "ac_mtom");
      return;
    }
    const maxSpeedErrorKey = maxSpeedValidationKey(formData.max_speed_ms, editingAircraft);
    if (maxSpeedErrorKey) {
      failValidation(t.fleet?.[maxSpeedErrorKey] || "Maximum speed must be a positive number of m/s.", "ac_max_speed");
      return;
    }

    // Explicit check because the SORA characteristic-dimension section is collapsible and unmounts its
    // input when closed — HTML's native `required` validation only fires for elements actually in the
    // DOM, so a user who collapses the section after opening the dialog (or reopens the edit dialog and
    // collapses it) could otherwise submit past it silently. formData itself is unaffected by the
    // section's open/closed state, so this checks the real value regardless of visibility.
    const dimensionRequired = !editingAircraft || editingAircraft?.max_characteristic_dimension_m != null;
    if (dimensionRequired && !formData.max_characteristic_dimension_m) {
      setDimensionSectionOpen(true);
      failValidation(t.fleet?.maxCharacteristicDimensionRequired || "Maximum characteristic dimension is required.", "ac_max_characteristic_dimension");
      return;
    }
    // Mirrors AircraftService.cs's own <= 0 rule (the only other way this specific value gets
    // rejected) — without this, a negative/zero value round-trips to the backend and back as a raw,
    // English-only AppError.Validation "detail" string (2026-09-16, user request: this is a distinct,
    // much broader gap — see handleSubmit's catch block below — not something one field check fixes
    // everywhere, but it does stop THIS specific message from ever being needed for this field).
    if (formData.max_characteristic_dimension_m && parseFloat(formData.max_characteristic_dimension_m) <= 0) {
      setDimensionSectionOpen(true);
      failValidation(t.fleet?.maxCharacteristicDimensionPositive || "Maximum characteristic dimension must be a positive number of metres.", "ac_max_characteristic_dimension");
      return;
    }

    try {
      if (editingAircraft) {
        // Build partial payload - only changed fields
        const payload = buildPartialPayload(editingAircraft, formData);
        
        // Always need at least one field to update
        if (Object.keys(payload).length === 0) {
          toast.info(t.messages?.noChanges || "No changes to save");
          setShowDialog(false);
          return;
        }
        
        await api.put(`/aircraft/${editingAircraft.id}`, payload);
        toast.success(t.messages.aircraftUpdated);
      } else {
        // New aircraft - send full payload
        const payload = {
          ...formData,
          organization_id: scopedOrganizationId,
          service_interval: parseInt(formData.service_interval),
          flight_hours: parseFloat(formData.flight_hours) || 0,
          mtom_kg: formData.mtom_kg ? parseFloat(formData.mtom_kg) : null,
          max_speed_ms: formData.max_speed_ms ? parseFloat(formData.max_speed_ms) : null,
          max_characteristic_dimension_m: formData.max_characteristic_dimension_m ? parseFloat(formData.max_characteristic_dimension_m) : null,
          legacy_drone: formData.legacy_drone || false
        };
        await api.post(`/aircraft`, payload);
        toast.success(t.messages.aircraftAdded);
      }
      setShowDialog(false);
      setEditingAircraft(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, language, t.messages.saveError));
    }
  };

  const handleDelete = async (ac) => {
    if (!window.confirm(`${t.actions.delete} ${ac.model} (${ac.serial_number})?`)) return;
    try {
      await api.delete(`/aircraft/${ac.id}`);
      toast.success(t.messages.aircraftDeleted);
      fetchData();
    } catch (error) {
      toast.error(t.messages.deleteError);
    }
  };

  const handleEdit = (ac) => {
    setEditingAircraft(ac);
    setFormData({
      organization_id: ac.organization_id,
      model: ac.model,
      serial_number: ac.serial_number,
      firmware_version: ac.firmware_version || "",
      service_interval: ac.service_interval || 100,
      flight_hours: ac.flight_hours || 0,
      last_maintenance_date: ac.last_maintenance_date?.split("T")[0] || "",
      registration_number: ac.registration_number || "",
      // NEW: MTOM and Legacy
      mtom_kg: ac.mtom_kg || "",
      max_speed_ms: ac.max_speed_ms ?? "",
      max_characteristic_dimension_m: ac.max_characteristic_dimension_m ?? "",
      legacy_drone: ac.legacy_drone || false,
      equipment: {
        parachute: ac.equipment?.parachute || false,
        flight_termination_system: ac.equipment?.flight_termination_system || false,
        geo_awareness: ac.equipment?.geo_awareness || false,
        remote_id: ac.equipment?.remote_id || false
      }
    });
    setManualDiagramKey(null);
    setDimensionSectionOpen(true);
    setShowDialog(true);
  };

  // Deep link from a SORA "Doplnit" CTA (see MissionDetail.js's navigateToProvisionalTarget) —
  // aircraft_dimension_missing/aircraft_speed_missing reasons and the detail page's missing-speed warning
  // navigate here with ?edit=<id> since
  // /aircraft/{id}'s own detail page renders these two fields read-only. Consumed once (param
  // cleared immediately) so a later reload of this exact URL doesn't reopen the modal.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || loading) return;
    const target = aircraft.find(ac => ac.id === editId);
    if (target) handleEdit(target);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('edit');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, aircraft, loading]);

  const resetForm = () => {
    setFormData({
      organization_id: scopedOrganizationId || "",
      model: "",
      serial_number: "",
      firmware_version: "",
      service_interval: 100,
      flight_hours: 0,
      last_maintenance_date: "",
      registration_number: "",
      // NEW: MTOM and Legacy
      mtom_kg: "",
      max_speed_ms: "",
      max_characteristic_dimension_m: "",
      legacy_drone: false,
      equipment: {
        parachute: false,
        flight_termination_system: false,
        geo_awareness: false,
        remote_id: false
      }
    });
    setManualDiagramKey(null);
    setDimensionSectionOpen(true);
  };

  // Document functions
  const openDocsDialog = (ac) => {
    setSelectedAircraft(ac);
    setShowDocsDialog(true);
  };

  const handleDocUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedAircraft) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        
        await api.post(
          `/aircraft/${selectedAircraft.id}/documents?document_type=${docType}`,
          formDataUpload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }
      
      // Refresh aircraft data
      const res = await api.get(`/aircraft/${selectedAircraft.id}`);
      setSelectedAircraft(res.data);
      
      // Update in list
      setAircraft(prev => prev.map(a => a.id === res.data.id ? res.data : a));
      
      toast.success(t.messages?.fileUploaded || "Document uploaded");
    } catch (error) {
      toast.error(t.messages?.uploadError || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await api.get(
        `/aircraft/${selectedAircraft.id}/documents/${doc.id}`,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t.messages?.downloadError || "Download failed");
    }
  };

  const deleteDocument = async (doc) => {
    if (!confirm(t.messages?.confirmDelete || "Are you sure?")) return;
    
    try {
      await api.delete(`/aircraft/${selectedAircraft.id}/documents/${doc.id}`);
      
      setSelectedAircraft(prev => ({
        ...prev,
        documents: prev.documents.filter(d => d.id !== doc.id)
      }));
      
      // Update in list
      setAircraft(prev => prev.map(a => {
        if (a.id === selectedAircraft.id) {
          return { ...a, documents: a.documents?.filter(d => d.id !== doc.id) || [] };
        }
        return a;
      }));
      
      toast.success(t.messages?.fileDeleted || "Document deleted");
    } catch (error) {
      toast.error(t.messages?.deleteError || "Delete failed");
    }
  };

  const isImageFile = (mimeType) => mimeType && mimeType.startsWith("image/");

  const getDocTypeLabel = (type) => {
    const labels = {
      registration: t.fleet?.registration || "Registration",
      maintenance: t.fleet?.maintenance || "Maintenance",
      inspection: t.fleet?.inspection || "Inspection",
      technical_certificate: t.fleet?.technicalCertificate || "Technical Certificate",
      other: t.common?.other || "Other"
    };
    return labels[type] || type;
  };

  // Block M-3: Build tooltip content for status badge
  const getStatusTooltipContent = (ac) => {
    const trigger = ac.maintenance_trigger || {};
    const reason = trigger.reason || "none";
    const tt = t.fleet?.statusTooltip || {};
    
    // Helper to interpolate values into template
    const interpolate = (template, current, limit) => {
      if (!template) return `${current}/${limit}`;
      return template.replace("{current}", current).replace("{limit}", limit);
    };
    
    // Missing date case
    if (trigger.days_since === null && ac.aircraft_status === "maintenance_required") {
      return (
        <div className="space-y-1 text-sm">
          <div className="font-medium">{tt.required || "Maintenance Required"}</div>
          <div className="flex items-center gap-1.5 text-slate-300">
            <Calendar className="h-3 w-3" />
            <span>{tt.missingDate || "Last maintenance date missing"}</span>
          </div>
        </div>
      );
    }
    
    const hoursLine = interpolate(
      tt.hoursReason, 
      Math.round(trigger.hours_used || 0), 
      trigger.interval_hours || 100
    );
    const calendarLine = interpolate(
      tt.calendarReason,
      trigger.days_since || 0,
      trigger.interval_days || 180
    );
    
    // Operational case - show both axes calmly
    if (reason === "none" || ac.aircraft_status === "operational") {
      return (
        <div className="space-y-1 text-sm">
          <div className="font-medium text-emerald-400">{tt.operational || "Operational"}</div>
          <div className="flex items-center gap-1.5 text-slate-300">
            <Clock className="h-3 w-3" />
            <span>{hoursLine}</span>
            <span className="text-slate-500">({Math.round(trigger.hours_pct || 0)}%)</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-300">
            <Calendar className="h-3 w-3" />
            <span>{calendarLine}</span>
            <span className="text-slate-500">({Math.round(trigger.days_pct || 0)}%)</span>
          </div>
        </div>
      );
    }
    
    // Build title based on status
    const title = ac.aircraft_status === "maintenance_required" 
      ? (tt.required || "Maintenance Required")
      : (tt.soon || "Maintenance Due Soon");
    const titleColor = ac.aircraft_status === "maintenance_required" 
      ? "text-rose-400" 
      : "text-amber-400";
    
    // Hours-driven
    if (reason === "hours") {
      return (
        <div className="space-y-1 text-sm">
          <div className={`font-medium ${titleColor}`}>{title}</div>
          <div className="flex items-center gap-1.5 text-white">
            <Clock className="h-3 w-3" />
            <span>{hoursLine}</span>
            <span className="text-slate-400">({Math.round(trigger.hours_pct || 0)}%)</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-400">
            <Calendar className="h-3 w-3" />
            <span>{calendarLine}</span>
            <span className="text-slate-500">({Math.round(trigger.days_pct || 0)}%)</span>
          </div>
        </div>
      );
    }
    
    // Calendar-driven
    if (reason === "calendar") {
      return (
        <div className="space-y-1 text-sm">
          <div className={`font-medium ${titleColor}`}>{title}</div>
          <div className="flex items-center gap-1.5 text-slate-400">
            <Clock className="h-3 w-3" />
            <span>{hoursLine}</span>
            <span className="text-slate-500">({Math.round(trigger.hours_pct || 0)}%)</span>
          </div>
          <div className="flex items-center gap-1.5 text-white">
            <Calendar className="h-3 w-3" />
            <span>{calendarLine}</span>
            <span className="text-slate-400">({Math.round(trigger.days_pct || 0)}%)</span>
          </div>
        </div>
      );
    }
    
    // Both axes
    return (
      <div className="space-y-1 text-sm">
        <div className={`font-medium ${titleColor}`}>{title}</div>
        <div className="text-xs text-slate-400 mb-1">{tt.bothAxes || "Both thresholds exceeded"}</div>
        <div className="flex items-center gap-1.5 text-white">
          <Clock className="h-3 w-3" />
          <span>{hoursLine}</span>
          <span className="text-slate-400">({Math.round(trigger.hours_pct || 0)}%)</span>
        </div>
        <div className="flex items-center gap-1.5 text-white">
          <Calendar className="h-3 w-3" />
          <span>{calendarLine}</span>
          <span className="text-slate-400">({Math.round(trigger.days_pct || 0)}%)</span>
        </div>
      </div>
    );
  };

  // Block M-3: Status badge with tooltip
  const getStatusBadgeWithTooltip = (ac) => {
    const status = ac.aircraft_status;
    const badge = status === "operational" 
      ? <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">{t.fleet.operational}</Badge>
      : status === "maintenance_soon"
      ? <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">{t.fleet.maintenanceSoon}</Badge>
      : <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400">{t.fleet.maintenanceRequired}</Badge>;
    
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help">{badge}</span>
        </TooltipTrigger>
        <TooltipContent side="left" className="bg-slate-900 border-slate-700 p-3">
          {getStatusTooltipContent(ac)}
        </TooltipContent>
      </Tooltip>
    );
  };

  const getStatusBadge = (status) => {
    if (status === "operational") return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">{t.fleet.operational}</Badge>;
    if (status === "maintenance_soon") return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">{t.fleet.maintenanceSoon}</Badge>;
    return <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400">{t.fleet.maintenanceRequired}</Badge>;
  };

  // Block I-2: Remote ID icon indicator (refactored from RemoteIDBadge pills)
  // Hierarchy: built_in > modular > exempted > non_compliant > unknown
  const RemoteIdIcon = ({ aircraft: ac }) => {
    const ridStatus = ac.rid_status;
    const isCompliant = ridStatus === "built_in" || ridStatus === "modular" || ac.remote_id_compliant === true;
    const isExempted = ridStatus === "exempted";
    const isNonCompliant = ridStatus === "non_compliant" || ac.remote_id_compliant === false;
    
    // Determine icon, color, and tooltip based on type
    let IconComponent = Minus;
    let iconClass = "text-slate-400";
    let tooltipText = t.fleet?.ridIcon?.unknown || "Unknown";
    
    if (ridStatus === "built_in") {
      IconComponent = Cpu;
      iconClass = "text-emerald-500";
      tooltipText = t.fleet?.ridIcon?.builtIn || "Built-in";
    } else if (ridStatus === "modular") {
      IconComponent = Puzzle;
      iconClass = "text-emerald-500";
      tooltipText = t.fleet?.ridIcon?.external || "External module";
    } else if (isCompliant) {
      // Generic compliant (legacy remote_id_compliant=true without rid_status)
      IconComponent = Check;
      iconClass = "text-emerald-500";
      tooltipText = t.fleet?.ridIcon?.compliant || "Compliant";
    } else if (isExempted) {
      IconComponent = Minus;
      iconClass = "text-amber-500";
      tooltipText = t.fleet?.ridIcon?.exempted || "Exempted";
    } else if (isNonCompliant) {
      IconComponent = X;
      iconClass = "text-rose-500";
      tooltipText = t.fleet?.ridIcon?.nonCompliant || "Non-compliant";
    }
    
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help inline-flex items-center justify-center">
            <IconComponent className={`h-4 w-4 ${iconClass}`} />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.company_name || "-";
  };

  // Block I-1: Compute status counts for filter chips (using same logic as before)
  const statusCounts = useMemo(() => {
    const required = aircraft.filter(a => a.aircraft_status === "maintenance_required").length;
    const soon = aircraft.filter(a => a.aircraft_status === "maintenance_soon").length;
    const operational = aircraft.filter(a => a.aircraft_status === "operational").length;
    return {
      all: aircraft.length,
      operational,
      maintenance_soon: soon,
      maintenance_required: required
    };
  }, [aircraft]);

  // Block I-1: Filter chips configuration
  const filterChipItems = useMemo(() => [
    { 
      id: "all", 
      label: t.fleet?.filterChips?.all || "All", 
      count: statusCounts.all, 
      variant: "neutral" 
    },
    { 
      id: "operational", 
      label: t.fleet?.filterChips?.operational || "Operational", 
      count: statusCounts.operational, 
      variant: "neutral" 
    },
    { 
      id: "maintenance_soon", 
      label: t.fleet?.filterChips?.maintenanceSoon || "Maintenance Soon", 
      count: statusCounts.maintenance_soon, 
      variant: "warning" 
    },
    { 
      id: "maintenance_required", 
      label: t.fleet?.filterChips?.maintenanceRequired || "Maintenance Required", 
      count: statusCounts.maintenance_required, 
      variant: "critical" 
    }
  ], [t.fleet?.filterChips, statusCounts]);

  // Block I-1: Combined filtering (status filter + search filter compose together)
  const filteredAircraft = useMemo(() => {
    return aircraft.filter(ac => {
      // Status filter
      const statusMatch = statusFilter === "all" || ac.aircraft_status === statusFilter;
      // Search filter (existing behavior preserved)
      const searchMatch = 
        ac.model.toLowerCase().includes(search.toLowerCase()) ||
        ac.serial_number.toLowerCase().includes(search.toLowerCase());
      return statusMatch && searchMatch;
    });
  }, [aircraft, statusFilter, search]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="aircraft-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
            {t.fleet.aircraftFleet}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t.pageDescriptions.aircraft}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowImportWizard(true)}
            className="gap-2"
            data-testid="import-aircraft-btn"
          >
            <FileSpreadsheet className="h-4 w-4" /> {t.actions?.import || 'Import'}
          </Button>
          <Button 
            onClick={() => { resetForm(); setEditingAircraft(null); setShowDialog(true); }}
            className="gap-2 bg-primary hover:bg-primary/90"
            data-testid="add-aircraft-btn"
          >
            <Plus className="h-4 w-4" /> {t.fleet.addAircraft}
          </Button>
        </div>
      </div>

      {/* Block I-1: Filter Chips (replaces old maintenance alert banners) */}
      <FilterChips
        items={filterChipItems}
        activeId={statusFilter}
        onSelect={setStatusFilter}
      />

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t.fleet.searchAircraft}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-aircraft"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <TooltipProvider>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>{t.fleet.model}</TableHead>
                {user?.role === "platform_admin" && <TableHead>{t.common.organization}</TableHead>}
                <TableHead>{t.fleet.serialNumber}</TableHead>
                <TableHead>{t.fleet?.uaCategory || "UA Class"}</TableHead>
                <TableHead>{t.fleet.registrationNumber}</TableHead>
                <TableHead>
                  <div className="flex items-center gap-1">
                    <Wrench className="h-4 w-4" />
                    {t.fleet?.serviceCycle || "Service Cycle"}
                  </div>
                </TableHead>
                <TableHead>{t.fleet.lastMaintenance}</TableHead>
                <TableHead className="w-16 text-center">{t.fleet?.ridIcon?.header || "Remote ID"}</TableHead>
                <TableHead>{t.common.status}</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={user?.role === "platform_admin" ? 11 : 10} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredAircraft.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={user?.role === "platform_admin" ? 11 : 10} className="text-center py-8">
                    <Plane className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">{t.fleet.noAircraftFound}</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAircraft.map((ac) => {
                  const isExpanded = expandedRowId === ac.id;
                  return (
                    <React.Fragment key={ac.id}>
                      <TableRow 
                        data-testid={`aircraft-row-${ac.id}`}
                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        onClick={() => setExpandedRowId(isExpanded ? null : ac.id)}
                      >
                        <TableCell className="w-8">
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {ac.model}
                          {missingSoraInputsKeys(ac) && (
                            <Badge
                              variant="outline"
                              className="ml-2 border-amber-500/50 text-amber-600 bg-amber-50 text-xs cursor-pointer"
                              title={t.fleet?.[missingSoraInputsKeys(ac).hint]}
                              onClick={(e) => { e.stopPropagation(); handleEdit(ac); }}
                              data-testid={`aircraft-sora-inputs-missing-${ac.id}`}
                            >
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {t.fleet?.[missingSoraInputsKeys(ac).label]}
                            </Badge>
                          )}
                        </TableCell>
                        {user?.role === "platform_admin" && (
                          <TableCell className="text-sm text-slate-500">{getOrgName(ac.organization_id)}</TableCell>
                        )}
                        <TableCell className="font-mono text-xs">{ac.serial_number}</TableCell>
                        <TableCell>
                          {ac.legacy_drone ? (
                            <Badge variant="outline" className="border-amber-500/50 text-amber-600 bg-amber-50 text-xs">
                              {t.fleet?.noClassMarking || "No marking"}
                            </Badge>
                          ) : ac.ua_class ? (
                            <Badge variant="outline" className="border-blue-500/50 text-blue-600 bg-blue-50 text-xs font-medium">
                              {ac.ua_class}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{ac.registration_number || <span className="text-rose-500">{t.fleet.unregistered}</span>}</TableCell>
                        <TableCell>
                          <ServiceCycleIndicator aircraft={ac} />
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(ac.last_maintenance_date)}</TableCell>
                        <TableCell className="text-center">
                          {/* Block I-2: Remote ID icon indicator (refactored from badge) */}
                          <RemoteIdIcon aircraft={ac} />
                        </TableCell>
                        <TableCell>{getStatusBadgeWithTooltip(ac)}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`aircraft-actions-${ac.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/aircraft/${ac.id}`)}>
                                <Eye className="h-4 w-4 mr-2" /> {t.actions?.view || "View"}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(ac)}>
                                <Pencil className="h-4 w-4 mr-2" /> {t.actions.edit}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setRidAircraft(ac); setShowRidModal(true); }}>
                                <Wifi className="h-4 w-4 mr-2" /> {t.remoteId?.title || "Remote ID"}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openDocsDialog(ac)}>
                                <FileText className="h-4 w-4 mr-2" /> {t.fleet?.documents || "Documents"}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(ac)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" /> {t.actions.delete}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                      {/* Block D: Expandable detail row */}
                      {isExpanded && (
                        <TableRow data-testid={`aircraft-detail-row-${ac.id}`}>
                          <TableCell colSpan={user?.role === "platform_admin" ? 11 : 10} className="p-0">
                            <AircraftExpandableRow 
                              aircraft={ac} 
                              isOpen={true}
                              onToggle={() => setExpandedRowId(null)}
                              onRefresh={fetchData}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
          </TooltipProvider>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-3xl lg:max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAircraft ? t.dialogs.editAircraft : t.dialogs.addAircraft}</DialogTitle>
          </DialogHeader>
          {/* noValidate: 2026-09-16, user request — HTML5 native constraint validation (required/step/
              min) shows the BROWSER's own bubble in the browser/OS locale, not the app's CS/EN toggle,
              and fires before this form's onSubmit handler even runs. Every required field below now
              has its own explicit, localized check in handleSubmit instead. */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="aircraft-form" noValidate>
            {/* Identification */}
            <section className="space-y-3">
              <FormGroupHeading>{t.fleet.formGroupIdentification}</FormGroupHeading>
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4${user?.role === "platform_admin" ? " lg:grid-cols-4" : ""}`}>
                {user?.role === "platform_admin" && (
                  <div className="space-y-2">
                    <Label>{t.common.organization} *</Label>
                    <Select
                      value={formData.organization_id}
                      disabled
                      onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                    >
                      <SelectTrigger data-testid="aircraft-organization">
                        <SelectValue placeholder={t.placeholders.selectOrganization} />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>{org.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="ac_model">{t.fleet.model} *</Label>
                  <Input
                    id="ac_model"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    required
                    data-testid="aircraft-model"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ac_serial">{t.fleet.serialNumber} *</Label>
                  <Input
                    id="ac_serial"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    required
                    data-testid="aircraft-serial"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ac_reg">{t.fleet.registrationNumber}</Label>
                  <Input
                    id="ac_reg"
                    value={formData.registration_number}
                    onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    placeholder={t.placeholders.registrationExample}
                    data-testid="aircraft-registration"
                  />
                </div>
              </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-6 gap-y-5">
              {/* Technical parameters: MTOM, maximum speed and the SORA characteristic dimension */}
              <section className="space-y-3 lg:col-span-2">
                <FormGroupHeading>{t.fleet.formGroupTechnical}</FormGroupHeading>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="ac_mtom">{t.fleet?.mtomKg || "MTOM (kg)"}{isMtomRequired(editingAircraft) ? " *" : ""}</Label>
                      <Input
                        id="ac_mtom"
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="150"
                        value={formData.mtom_kg}
                        onChange={(e) => setFormData({ ...formData, mtom_kg: e.target.value })}
                        placeholder={t.fleet.mtomPlaceholder}
                        required={isMtomRequired(editingAircraft)}
                        data-testid="aircraft-mtom"
                      />
                      <p className="text-xs text-[var(--text-muted)]">{t.fleet[mtomHelperKey(editingAircraft)]}</p>
                      {editingAircraft && isMtomMissing(editingAircraft) && (
                        <p className="text-xs text-amber-600 flex items-start gap-1" data-testid="aircraft-mtom-missing-hint">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          {t.fleet?.mtomMissingDialogHint}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ac_max_speed">{t.fleet.maxSpeedMs}{isMaxSpeedRequired(editingAircraft) ? " *" : ""}</Label>
                      <Input
                        id="ac_max_speed"
                        type="number"
                        step="any"
                        min="0.01"
                        value={formData.max_speed_ms}
                        onChange={(e) => setFormData({ ...formData, max_speed_ms: e.target.value })}
                        required={isMaxSpeedRequired(editingAircraft)}
                        data-testid="aircraft-max-speed"
                      />
                      <p className="text-xs text-[var(--text-muted)]">{t.fleet[maxSpeedHelperKey(editingAircraft)]}</p>
                      {editingAircraft && isMaxSpeedMissing(editingAircraft) && (
                        <p className="text-xs text-amber-600 flex items-start gap-1" data-testid="aircraft-max-speed-missing-hint">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          {t.fleet?.maxSpeedMissingDialogHint}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* SORA Characteristic Dimension Section (visually akin to Missions.js's U-Space section) */}
                  <div
                    className="rounded-lg border-2 border-blue-200 bg-blue-50/30 dark:bg-blue-950/20 dark:border-blue-800/50 overflow-hidden"
                    data-testid="characteristic-dimension-section"
                  >
                    <button
                      type="button"
                      onClick={() => setDimensionSectionOpen(!dimensionSectionOpen)}
                      className="w-full flex items-center justify-between p-3 hover:bg-blue-100/50 dark:hover:bg-blue-900/30 transition-colors"
                      aria-expanded={dimensionSectionOpen}
                    >
                      <div className="flex items-center gap-2">
                        <Ruler className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        <div className="text-left">
                          <span className="font-medium text-sm text-slate-900 dark:text-slate-100">
                            {t.fleet?.characteristicDimensionSection || "SORA characteristic dimension"}
                          </span>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {t.fleet?.characteristicDimensionSectionSubtitle || "Used by the quantitative SORA 2.5 ground-risk table"}
                          </p>
                        </div>
                      </div>
                      {dimensionSectionOpen ? (
                        <ChevronUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      )}
                    </button>

                    {dimensionSectionOpen && (
                      <div className="p-3 pt-0 border-t border-blue-200 dark:border-blue-800/50">
                        <div className="mt-3">
                          <CharacteristicDimensionField
                            t={t}
                            value={formData.max_characteristic_dimension_m}
                            onChange={(val) => setFormData({ ...formData, max_characteristic_dimension_m: val })}
                            required={!editingAircraft || editingAircraft?.max_characteristic_dimension_m != null}
                            lockedHelper={editingAircraft?.max_characteristic_dimension_m != null
                              ? (t.fleet?.maxCharacteristicDimensionLockedHelper || "Already recorded for this aircraft — clearing it isn't supported here to avoid losing a SORA-relevant value.")
                              : (!editingAircraft ? (t.fleet?.maxCharacteristicDimensionRequired || "Required for new aircraft — used by the SORA 2.5 ground-risk table.") : undefined)}
                            catalogDroneType={editingAircraft?.catalog_drone_type || null}
                            manualDiagramKey={manualDiagramKey}
                            onManualDiagramKeyChange={setManualDiagramKey}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* UA class (computed from MTOM) and drones without a class marking */}
              <section className="space-y-3">
                <FormGroupHeading>{t.fleet.formGroupClass}</FormGroupHeading>
                <div className="space-y-2">
                  <Label>{t.fleet?.uaCategory || "UA Category"}</Label>
                  {formData.legacy_drone ? (
                    <div className="h-10 flex items-center">
                      <Badge variant="outline" className="border-amber-500/50 text-amber-600 bg-amber-50">
                        {t.fleet?.noClassMarking || "No class marking"}
                      </Badge>
                    </div>
                  ) : (
                    <div className="h-10 flex items-center">
                      {computeUaClass(formData.mtom_kg, formData.legacy_drone) ? (
                        <Badge variant="outline" className="border-blue-500/50 text-blue-600 bg-blue-50 text-lg px-3">
                          {computeUaClass(formData.mtom_kg, formData.legacy_drone)}
                        </Badge>
                      ) : (
                        <span className="text-sm text-[var(--text-muted)]">
                          {t.fleet.uaCategoryEnterMtom}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-start space-x-3 p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800/30">
                  <Checkbox
                    id="legacy_drone"
                    checked={formData.legacy_drone || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, legacy_drone: checked })}
                    data-testid="aircraft-legacy-drone"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="legacy_drone" className="text-sm font-medium cursor-pointer">
                      {t.fleet?.legacyDrone || "Drone without C0-C6 class marking"}
                    </Label>
                    <p className="text-xs text-[var(--text-muted)]">
                      {t.fleet?.legacyDroneHelper || "Older or custom-built drone without EU class marking"}
                    </p>
                  </div>
                </div>
              </section>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-6 gap-y-5">
              {/* Maintenance and operation */}
              <section className="space-y-3 lg:col-span-2">
                <FormGroupHeading>{t.fleet.formGroupMaintenance}</FormGroupHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ac_firmware">{t.fleet.firmwareVersion}</Label>
                    <Input
                      id="ac_firmware"
                      value={formData.firmware_version}
                      onChange={(e) => setFormData({ ...formData, firmware_version: e.target.value })}
                      data-testid="aircraft-firmware"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ac_interval">{t.fleet.serviceInterval}</Label>
                    <Input
                      id="ac_interval"
                      type="number"
                      value={formData.service_interval}
                      onChange={(e) => setFormData({ ...formData, service_interval: e.target.value })}
                      data-testid="aircraft-interval"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ac_flight_hours">{t.fleet?.flightHours || "Flight Hours"}</Label>
                    <Input
                      id="ac_flight_hours"
                      type="number"
                      step="0.1"
                      value={formData.flight_hours}
                      onChange={(e) => setFormData({ ...formData, flight_hours: e.target.value })}
                      data-testid="aircraft-flight-hours"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ac_maintenance">{t.fleet.lastMaintenance}</Label>
                    <Input
                      id="ac_maintenance"
                      type="date"
                      value={formData.last_maintenance_date}
                      onChange={(e) => setFormData({ ...formData, last_maintenance_date: e.target.value })}
                      data-testid="aircraft-maintenance"
                    />
                  </div>
                </div>
              </section>

              {/* SORA Equipment Section */}
              <section className="space-y-3">
                <FormGroupHeading>{t.fleet?.soraEquipment || "SORA Equipment"}</FormGroupHeading>
                <p className="text-xs text-slate-500">
                  {t.fleet?.soraEquipmentHint || "Select installed safety equipment for SORA pre-assessment"}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="eq_parachute"
                      checked={formData.equipment?.parachute || false}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        equipment: { ...formData.equipment, parachute: checked }
                      })}
                      data-testid="aircraft-parachute"
                    />
                    <Label htmlFor="eq_parachute" className="text-sm font-normal cursor-pointer">
                      {t.fleet?.parachute || "Parachute"}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="eq_fts"
                      checked={formData.equipment?.flight_termination_system || false}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        equipment: { ...formData.equipment, flight_termination_system: checked }
                      })}
                      data-testid="aircraft-fts"
                    />
                    <Label htmlFor="eq_fts" className="text-sm font-normal cursor-pointer">
                      {t.fleet?.fts || "Flight Termination System"}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="eq_geo"
                      checked={formData.equipment?.geo_awareness || false}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        equipment: { ...formData.equipment, geo_awareness: checked }
                      })}
                      data-testid="aircraft-geo-awareness"
                    />
                    <Label htmlFor="eq_geo" className="text-sm font-normal cursor-pointer">
                      {t.fleet?.geoAwareness || "Geo-awareness"}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="eq_remote_id"
                      checked={formData.equipment?.remote_id || false}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        equipment: { ...formData.equipment, remote_id: checked }
                      })}
                      data-testid="aircraft-remote-id"
                    />
                    <Label htmlFor="eq_remote_id" className="text-sm font-normal cursor-pointer">
                      {t.fleet?.remoteId || "Remote ID"}
                    </Label>
                  </div>
                </div>
              </section>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>{t.actions.cancel}</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="aircraft-submit-btn">
                {editingAircraft ? t.actions.update : t.actions.create}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={showDocsDialog} onOpenChange={setShowDocsDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t.fleet?.documents || "Documents"} - {selectedAircraft?.model} ({selectedAircraft?.serial_number})
            </DialogTitle>
          </DialogHeader>
          
          {/* Upload Section */}
          <div className="border-b pb-4 mb-4">
            <div className="flex items-center gap-4">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="registration">{t.fleet?.registration || "Registration"}</SelectItem>
                  <SelectItem value="maintenance">{t.fleet?.maintenance || "Maintenance"}</SelectItem>
                  <SelectItem value="inspection">{t.fleet?.inspection || "Inspection"}</SelectItem>
                  <SelectItem value="technical_certificate">{t.fleet?.technicalCertificate || "Technical Certificate"}</SelectItem>
                  <SelectItem value="other">{t.common?.other || "Other"}</SelectItem>
                </SelectContent>
              </Select>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleDocUpload}
                className="hidden"
                multiple
                accept="image/*,.pdf,.doc,.docx"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="gap-2"
              >
                {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? (t.common?.uploading || "Uploading...") : (t.actions?.upload || "Upload")}
              </Button>
            </div>
          </div>

          {/* Documents List */}
          {(!selectedAircraft?.documents || selectedAircraft.documents.length === 0) ? (
            <div className="text-center py-8 text-slate-500">
              <Paperclip className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <p>{t.fleet?.noDocuments || "No documents uploaded"}</p>
              <p className="text-xs mt-1">{t.fleet?.uploadHint || "Upload registration, maintenance records, or inspection documents"}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedAircraft.documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-3 min-w-0">
                    {isImageFile(doc.mime_type) ? (
                      <Image className="h-8 w-8 text-blue-500 flex-shrink-0" />
                    ) : (
                      <FileText className="h-8 w-8 text-slate-500 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.file_name}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">{getDocTypeLabel(doc.document_type)}</Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(doc.upload_timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadDocument(doc)}
                      className="h-8 w-8 p-0"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteDocument(doc)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Wizard */}
      <ImportWizard
        open={showImportWizard}
        onOpenChange={setShowImportWizard}
        entityType="aircraft"
        onComplete={() => fetchData()}
      />

      {/* Wave 2B: Remote ID Modal */}
      <RemoteIDStatusModal
        open={showRidModal}
        onOpenChange={setShowRidModal}
        aircraft={ridAircraft}
        onSuccess={() => fetchData()}
      />
    </div>
  );
};
